import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Avatar,
  Tooltip,
  Typography,
  Divider,
  Menu,
  MenuItem,
  alpha,
  useTheme,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import logo from '../../../assets/genius_logo.svg';
import { useNavigate } from 'react-router-dom';

export default function Header() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleProfileOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleProfileClose = () => setAnchorEl(null);

  const storedUser = localStorage.getItem("user_details_from_server");
  const userDetails = storedUser ? JSON.parse(storedUser) : null;
  const fullName = userDetails?.[0]?.firstName + " " + userDetails?.[0]?.lastName;

  const handleLogout = () => {
    localStorage.clear();
    handleProfileClose();
    navigate('/login', { replace: true });
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: '100%',
        height: 70,
        justifyContent: 'center',
        backgroundColor: alpha('#FFFFFF', 0.9),
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
      }}
    >
      <Toolbar sx={{ px: { xs: 2, sm: 3 }, gap: 1 }}>
        {/* ── Logo + title ─────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src={logo}
            alt="Genius logo"
            sx={{
              width: '110px',
              height: '32px',
              aspectRatio: '55/16',
              borderRadius: '10px',
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              Live Insights Dashboard
            </Typography>
          </Box>
        </Box>

        {/* ── Spacer ──────────────────────────────────── */}
        <Box sx={{ flexGrow: 1 }} />

        {/* ── Right actions ───────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {fullName}
          </Typography>
          {/* Profile avatar */}
          <Tooltip title="Account">
              <IconButton
              id="profile-menu-btn"
              aria-label="account menu"
              onClick={handleProfileOpen}
              sx={{ p: 0.5, borderRadius: '12px' }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  boxShadow: `0 2px 10px 0 ${alpha(theme.palette.primary.main, 0.35)}`,
                }}
              >
                {fullName[0]}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Profile dropdown menu ───────────────────── */}
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 1.5,
              minWidth: 200,
              borderRadius: '12px',
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              boxShadow: '0 8px 24px 0 rgba(32,40,45,0.12)',
              overflow: 'visible',
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 18,
                width: 10,
                height: 10,
                backgroundColor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                borderRight: 'none',
                borderBottom: 'none',
              },
            },
          }}
        >
          {/* User info header */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {fullName}
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            id="logout-menu-item"
            onClick={handleLogout}
            sx={{ py: 1.25, gap: 1.5, borderRadius: 1, mx: 0.5, mb: 0.5, color: 'error.main' }}
          >
            <LogoutIcon fontSize="small" />
            <Typography variant="body2" color="error">
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
